import { useEffect, useRef, useState } from "react";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import Select from "../../../../components/input/Select";
import { SimpleOption } from "../../../../components/input/SimpleListbox";
import { tooltipMark, formatNumberOneDecimal } from "../../shared/reportUtil";
import { ReportLevel } from "../adminReportsApiSlice";
import { reportSubLevelName, threeBarAndLineChartColors } from "../adminReportsUtil";
import { CoreWritingStandardRow } from "./adminPerformanceReportApiSlice";

const seriesTypes = ["Argumentative", "Informative/Explanatory", "Narrative", "Average"];

const seriesOptions: SimpleOption[] = [
	{ value: "all", label: "All Genres" },
	{ value: seriesTypes[0], label: seriesTypes[0] },
	{ value: seriesTypes[1], label: seriesTypes[1] },
	{ value: seriesTypes[2], label: seriesTypes[2] },
	{ value: seriesTypes[3], label: seriesTypes[3] },
];

export function GenreAverageScoresChart(props: {
	level: ReportLevel;
	data: CoreWritingStandardRow[];
	hasData: boolean;
}) {
	const [focusedSeries, setFocusedSeries] = useState("all");

	const chartRef = useRef<ReactEcharts>(null);
	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	let chartColors = chartPalette;
	if (!chartPalette) {
		chartColors = threeBarAndLineChartColors;
	}

	const getDisplayedSeries = () => {
		if (focusedSeries === "all") {
			return {
				[seriesTypes[0]]: true,
				[seriesTypes[1]]: true,
				[seriesTypes[2]]: true,
				[seriesTypes[3]]: true,
			};
		}

		// Only set the selected series from the dropdown to true, hide the rest
		return {
			[seriesTypes[0]]: focusedSeries === seriesTypes[0],
			[seriesTypes[1]]: focusedSeries === seriesTypes[1],
			[seriesTypes[2]]: focusedSeries === seriesTypes[2],
			[seriesTypes[3]]: focusedSeries === seriesTypes[3],
		};
	};

	const initialEChartOptions: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		title: {
			text: `${reportSubLevelName(props.level)} Average Genre Scores`,
			left: "center",
			textStyle: { fontSize: 16, fontWeight: 600, color: foregroundColor },
		},
		legend: {
			selected: getDisplayedSeries(),
			selectedMode: false,
			top: 35,
			textStyle: {
				color: foregroundColor,
			},
		},
		grid: {
			containLabel: true,
			left: 0,
			right: 40,
			bottom: 30,
			top: 75,
		},
		xAxis: {
			type: "value",
			show: true,
			name: "Average Score",
			nameLocation: "middle",
			nameGap: 30,
			min: 0,
			minInterval: 1,
			max: 30,
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
					width: 1,
				},
			},
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		yAxis: {
			type: "category",
			data: props.data.map((row) => row.name),
			inverse: true, // Forces the order of the rows in the table on initial load
			axisPointer: { type: "shadow" },
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			axisLabel: {
				padding: 10,
				lineHeight: 15,
				interval: 0,
				width: 180,
				overflow: "break",
				hideOverlap: false,
			},
		},
		tooltip: {
			trigger: "axis",
			formatter: (
				params: {
					axisValue: string;
					dataIndex: number;
					color: string;
					seriesName: string;
					value: string;
				}[]
			) => {
				if (!params.find((p) => p.value)) return;

				const label = `<div>${props.data[params[0].dataIndex].name}</div>`;
				const lineItem = (p: any) => {
					return `<div>${tooltipMark(p.color)} ${p.seriesName}: <b>${formatNumberOneDecimal(Number(p.value))}</b></div>`;
				};
				const content = params.reduce((a: string, v: any) => a + lineItem(v), label);

				return content;
			},
			textStyle: {
				fontSize: 12,
			},
		},
		animationDurationUpdate: 500,
		series: [],
	};

	useEffect(() => {
		const options = updateOptions(
			initialEChartOptions,
			buildSeriesOptions(props.data, focusedSeries, foregroundColor, chartColors)
		);

		if (chartPalette != undefined) {
			options.color = chartPalette;
		}

		chartRef.current?.getEchartsInstance().setOption(options, true);
	}, [props.data, chartPalette, foregroundColor, accentColor, focusedSeries]);

	const chartHeight = props.data.length * 70 + 150;

	return (
		<>
			{props.hasData && (
				<div className="relative w-full mt-4 p-4 border-2 border-gray-200">
					<div className="z-10 absolute top-4 left-4">
						<Select
							simpleListBoxId="series-filter"
							value={focusedSeries}
							setValue={(value) => setFocusedSeries(value as string)}
							options={seriesOptions}
							fieldClasses="flex items-center gap-3"
							simpleListBoxClasses="w-64"
						/>
					</div>

					<ReactEcharts
						ref={chartRef}
						option={initialEChartOptions}
						className="w-full !min-h-96"
						style={{
							height: chartHeight,
						}}
					/>
				</div>
			)}
		</>
	);
}

function buildSeriesOptions(
	data: CoreWritingStandardRow[],
	focusedSeries: string,
	foregroundColor: string,
	chartColors: string[]
): EChartsOption["series"] {
	function makeBarSeries(data: number[], index: number) {
		return {
			name: seriesTypes[index],
			type: "bar",
			cursor: "default",
			data: data,
			itemStyle: { color: chartColors[index] },
			barMinWidth: 15,
			emphasis: {
				itemStyle: {
					shadowBlur: 7,
				},
			},
			label: {
				show: true,
				fontWeight: "bold",
				color: foregroundColor,
				formatter: (params: any) => {
					return params.value > 0 ? formatNumberOneDecimal(params.value) : "";
				},
				position: "right",
			},
		};
	}
	return [
		makeBarSeries(
			data.map((row) => row.averageArgumentative),
			0
		),
		makeBarSeries(
			data.map((row) => row.averageInformativeExplanatory),
			1
		),
		makeBarSeries(
			data.map((row) => row.averageNarrative),
			2
		),
		{
			name: seriesTypes[3],
			type: "line",
			cursor: "default",
			connectNulls: true,
			data: data.map((row) => row.average),
			itemStyle: { color: chartColors[3] },
			lineStyle: { width: 3 },
			symbolSize: 10,
			emphasis: {
				itemStyle: {
					shadowBlur: 7,
				},
			},
			label: {
				show: focusedSeries === seriesTypes[3],
				color: foregroundColor,
				fontWeight: "bold",
				formatter: (params: any) => {
					return params.value > 0 ? formatNumberOneDecimal(params.value) : "";
				},
			},
		},
	];
}

function updateOptions(options: EChartsOption, seriesData: EChartsOption["series"]): EChartsOption {
	options.series = seriesData;
	return options;
}
